<template>
  <!--
    Where a company / department store's order ships (Jake, 2026-10-20).
    The shopper enters no address on these stores, so the checkout says
    where the order goes and what that address IS. The address and its
    meaning come from the SERVER (`checkout_quote` -> `ship_to`), the same
    resolution the order freezes; nothing here is entered or editable.
    Every value renders through text interpolation (escaped); never v-html.
  -->
  <div class="ship-to-destination">
    <div
      v-if="state === 'loading' && !shipTo"
      class="text-grey"
      data-testid="co-ship-to-loading"
    >
      Loading shipping destination...
    </div>
    <div
      v-else-if="state === 'awaiting-department'"
      class="text-grey"
      data-testid="co-ship-to-awaiting"
    >
      Choose a department to see where this order ships.
    </div>
    <div
      v-else-if="state === 'failed' && !shipTo"
      class="text-error"
      data-testid="co-ship-to-failed"
    >
      We could not load where this order ships. Please refresh the page to
      try again.
    </div>
    <div
      v-else-if="shipTo && shipTo.complete === false"
      class="text-error"
      data-testid="co-ship-to-missing"
    >
      This store has no complete shipping address on file, so the order
      cannot be placed yet. Please contact the store.
    </div>
    <div v-else-if="shipTo && shipTo.address" data-testid="co-ship-to">
      <div class="font-weight-medium" data-testid="co-ship-to-meaning">
        {{ meaning }}
      </div>
      <!--
        "Ships to <name, address>, set by <store>" (card #402): the server's
        sentence, printed as sent. It states the address, so the address is
        not printed a second time; a payload without it (an older server)
        falls back to the address lines.
      -->
      <div
        v-if="disclosure"
        class="md-body"
        data-testid="co-ship-to-disclosure"
      >
        {{ disclosure }}
      </div>
      <div v-else class="md-body" data-testid="co-ship-to-address">
        <div v-for="(addressLine, i) in addressLines" :key="i">
          {{ addressLine }}
        </div>
      </div>
      <div class="text-caption text-grey">
        You do not need to enter an address.
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CartShipToDestination",

  props: {
    /** `checkout_quote`'s `ship_to`, or null before it has loaded. */
    shipTo: { type: Object, default: null },
    /**
     * "idle" | "loading" | "loaded" | "failed" | "awaiting-department" --
     * the quote's progress. "awaiting-department": the store needs the
     * shopper's department pick before the server can resolve anything.
     */
    state: { type: String, default: "idle" },
  },

  computed: {
    /**
     * What the address IS ("Shipping to your company's address"). The line
     * is the SERVER's (`ship_to.meaning`, from
     * `services/store_ship_to.ship_to_meaning`), the same string the
     * order-confirmation email prints, so this keeps no label table of its
     * own. No meaning sent: the plain heading, nothing guessed.
     */
    meaning() {
      const meaning = this.shipTo && this.shipTo.meaning;
      return typeof meaning === "string" && meaning.trim()
        ? meaning
        : "Shipping to";
    },

    /** The server's "Ships to ..., set by <store>" sentence, or "". */
    disclosure() {
      const v = this.shipTo && this.shipTo.disclosure;
      return typeof v === "string" && v.trim() ? v : "";
    },

    addressLines() {
      const a = (this.shipTo && this.shipTo.address) || {};
      const clean = (v) => (typeof v === "string" ? v.trim() : "");
      const stateZip = [clean(a.state), clean(a.postal_code)]
        .filter(Boolean)
        .join(" ");
      const cityLine = [clean(a.city), stateZip].filter(Boolean).join(", ");
      const country = clean(a.country);
      return [
        clean(a.recipient_name),
        clean(a.address1),
        clean(a.address2),
        cityLine,
        // US is not printed, the way a US shopper writes an address.
        country && country.toUpperCase() !== "US" ? country : "",
      ].filter(Boolean);
    },
  },
};
</script>
